import React from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { StopCategory } from '../../services/stopGameValidator';

interface StopCategoryInputProps {
  category: StopCategory;
  letter: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const StopCategoryInput: React.FC<StopCategoryInputProps> = ({
  category,
  letter,
  value,
  onChange,
  disabled = false,
  autoFocus = false
}) => {
  const cleanVal = value.trim();
  const targetLetter = letter.trim().toLowerCase();

  const startsWithCorrectLetter = cleanVal.length > 0 && cleanVal.toLowerCase().startsWith(targetLetter);
  const startsWithWrongLetter = cleanVal.length > 0 && !cleanVal.toLowerCase().startsWith(targetLetter);

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:border-purple-200 transition-all focus-within:ring-2 focus-within:ring-purple-400 focus-within:border-transparent">
      <div className="flex items-center justify-between mb-2">
        <label
          htmlFor={`input-${category.id}`}
          className="flex items-center space-x-2 text-sm font-bold text-gray-800"
        >
          <span className="text-xl" role="img" aria-label={category.name}>
            {category.icon}
          </span>
          <span>{category.name}</span>
        </label>
        <span className="text-xs text-gray-500 font-medium hidden sm:inline">
          {category.description}
        </span>
      </div>

      <div className="relative">
        <input
          id={`input-${category.id}`}
          type="text"
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={e => onChange(e.target.value)}
          placeholder={`Starts with "${letter.toUpperCase()}"...`}
          className={`w-full px-3.5 py-2.5 text-base rounded-xl border transition-colors outline-none pr-10 font-medium ${
            disabled
              ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
              : startsWithCorrectLetter
              ? 'border-emerald-400 bg-emerald-50/30 text-gray-900 focus:bg-white'
              : startsWithWrongLetter
              ? 'border-amber-400 bg-amber-50/30 text-gray-900 focus:bg-white'
              : 'border-gray-200 bg-white text-gray-900 focus:border-purple-500'
          }`}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        />

        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
          {startsWithCorrectLetter && (
            <CheckCircle2 className="w-5 h-5 text-emerald-500 animate-in zoom-in" />
          )}
          {startsWithWrongLetter && (
            <AlertCircle className="w-5 h-5 text-amber-500" />
          )}
        </div>
      </div>

      {startsWithWrongLetter && (
        <p className="mt-1 text-xs text-amber-600 font-medium">
          Must begin with the letter <strong className="uppercase">{letter}</strong>
        </p>
      )}
    </div>
  );
};

export default StopCategoryInput;
